import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_URL } from '../config/Config';
import { buildLoginPath } from '../utils/controllers/oauthRedirect';

// Create a base query with common settings
const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState, endpoint, extra, requestId }) => {
    // Check if this endpoint needs custom token handling
    const customTokenEndpoints = ['getAllUserDomains', 'getCustomerReport'];

    if (customTokenEndpoints.includes(endpoint)) {
      // For these endpoints, don't set default token - let the endpoint handle it
      return headers;
    }

    // Use default access token from localStorage for other endpoints
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return headers;
  },
});

// Enhanced base query with 401 handling
const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // If we get a 401 response, redirect to login
  if (result.error && result.error.status === 401) {
    // Clear any stored tokens
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    // Preserve Claude OAuth context only when it's a real authorize URL
    window.location.href = buildLoginPath(window.location.search);
  }

  return result;
};

// Create the API using Redux Toolkit's createApi
export const bridgedApi = createApi({
  reducerPath: 'bridgedApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'userInfo',
    'users',
    'defaultChecklists',
    'userChecklists',
    'taskComments',
    'userConfiguration',
    'companies',
    'teams',
    'teamCredits',
    'teamPlaybooks',
    'agentReports',
  ],
  endpoints: builder => ({
    // User login mutation
    login: builder.mutation({
      query: data => ({
        url: '/AdminUser/Login',
        method: 'POST',
        body: data,
      }),
    }),

    userInfo: builder.query({
      query: () => '/User/Profile',
      providesTags: ['userInfo'],
    }),

    findAllUsers: builder.query({
      query: () => '/User/Admin/FindAllUsers',
      providesTags: ['users'],
    }),

    findAllUsersPagination: builder.mutation({
      query: ({ companyId, status, sort, search, page, limit }) => ({
        url: `/User/Admin/FindAllUsers?companyId=${companyId}&status=${status}&sort=${sort}&search=${search}&page=${page}&limit=${limit}`,
        method: 'POST',
        body: { companyId, status, sort, search, page, limit },
      }),
      invalidatesTags: ['users'],
    }),

    getUserAdminPagination: builder.mutation({
      query: ({ companyId, teamId, status, sort, search, pageNumber, limit }) => ({
        url: '/User/Admin/Pagination',
        method: 'POST',
        body: {
          ...(companyId && { companyId }),
          ...(teamId && { teamId }),
          status: status || 'all',
          sort: sort || 'lastLogin_DESC',
          search: search || '',
          pageNumber: pageNumber || 1,
          limit: limit || 10,
        },
      }),
      providesTags: ['users'],
    }),

    getUsersWithNoTeam: builder.query({
      query: () => ({
        url: '/User/Admin/NoTeam',
        method: 'GET',
      }),
    }),

    addUser: builder.mutation({
      query: data => ({
        url: '/User/Admin/CreateByAdmin',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['users'],
    }),

    getUserForUpdateByAdmin: builder.query({
      query: userId => ({
        url: `/User/Admin/GetUserForUpdate?userId=${userId}`,
        method: 'GET',
      }),
      providesTags: (result, error, userId) => [{ type: 'users', id: userId }],
    }),

    updateUserByAdmin: builder.mutation({
      query: ({ userId, data }) => ({
        url: `/User/Admin/UpdateByAdmin?userId=${userId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['users'],
    }),

    generateUserToken: builder.mutation({
      query: data => ({
        url: '/User/Admin/GenerateTokenForUserByAdmin',
        method: 'POST',
        body: data,
      }),
    }),

    getAllUserDomains: builder.mutation({
      query: ({ customToken }) => ({
        url: '/Domain/FindAll',
        method: 'GET',
        headers: customToken
          ? {
              Authorization: `Bearer ${customToken}`,
            }
          : {},
      }),
    }),

    getCustomerReport: builder.mutation({
      query: ({ data, customToken }) => ({
        url: '/Campaign/dataExport',
        method: 'POST',
        body: data,
        headers: customToken
          ? {
              Authorization: `Bearer ${customToken}`,
            }
          : {},
      }),
    }),

    getResearchPartnerDate: builder.mutation({
      query: data => ({
        url: 'https://ai-agents-api.bridged.media/api/rp_agent/data_export',
        method: 'POST',
        body: data,
      }),
    }),

    getDiscoveryAgentData: builder.mutation({
      query: data => ({
        url: 'https://ai-agents-api.bridged.media/api/discovery_agent/data_export',
        method: 'POST',
        body: data,
      }),
    }),

    getSummaryUsageData: builder.mutation({
      query: data => ({
        url: 'https://summary-agent.bridged.media/exportSummaryUsageData',
        method: 'POST',
        body: data,
      }),
    }),

    getSEOV3Data: builder.mutation({
      query: data => ({
        url: 'https://seo-agent.bridged.media/exportSEOV3UsageData',
        method: 'POST',
        body: data,
      }),
    }),

    getSEOV4Data: builder.mutation({
      query: data => ({
        url: 'https://seo-agent.bridged.media/exportSEOV4UsageData',
        method: 'POST',
        body: data,
      }),
    }),

    getBacklinkUsageData: builder.mutation({
      query: data => ({
        url: 'https://backlink-agent.bridged.media/exportBacklinkUsageData',
        method: 'POST',
        body: data,
      }),
    }),

    getDefaultChecklist: builder.query({
      query: () => ({
        url: '/checklists/Admin/DefaultChecklists',
        method: 'GET',
      }),
      providesTags: ['defaultChecklists'],
    }),

    addDefaultChecklist: builder.mutation({
      query: data => ({
        url: '/checklists/Admin',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['defaultChecklists', 'userChecklists'],
    }),

    deleteDefaultChecklist: builder.mutation({
      query: id => ({
        url: `/checklists/Admin/?_id=${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['defaultChecklists'],
    }),

    deleteUserChecklist: builder.mutation({
      query: id => ({
        url: `/checklists/Admin?_id=${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['userChecklists'],
    }),

    updateDefaultChecklist: builder.mutation({
      query: ({ id, data }) => ({
        url: `/checklists/Admin/?_id=${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['defaultChecklists'],
    }),

    updateUserChecklist: builder.mutation({
      query: ({ id, data }) => ({
        url: `/checklists/Admin?_id=${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['userChecklists'],
    }),

    uploadImage: builder.mutation({
      query: data => ({
        url: '/Media/Admin/Upload?type=image',
        method: 'POST',
        body: data,
      }),
    }),

    createTask: builder.mutation({
      query: data => ({
        url: `/tasks/Admin`,
        method: 'POST',
        body: data,
        invalidatesTags: ['defaultChecklists'],
      }),
    }),

    updateTask: builder.mutation({
      query: ({ id, data }) => ({
        url: `/tasks/Admin/?_id=${id}`,
        method: 'PUT',
        body: data,
      }),
    }),

    deleteTask: builder.mutation({
      query: id => ({
        url: `/tasks/Admin/?_id=${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['defaultChecklists'],
    }),

    getUserChecklist: builder.query({
      query: id => ({
        url: `/checklists/Admin/UsersChecklists/?userId=${id}`,
        method: 'GET',
      }),
      providesTags: ['userChecklists'],
    }),

    getTeamMembers: builder.query({
      query: id => ({
        url: `/Team/Admin/GetTeamMembers?userId=${id}`,
        method: 'GET',
      }),
      providesTags: ['teamMembers'],
    }),

    addTeamMember: builder.mutation({
      query: data => ({
        url: `/Team/Admin/TeamMember`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['teamMembers'],
    }),

    deleteTeamMember: builder.mutation({
      query: id => ({
        url: `/Team/Admin/TeamMember?_id=${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['teamMembers'],
    }),

    getUserChecklistTaskComments: builder.query({
      query: taskId => ({
        url: `/TaskComments/Admin/FindAllByTaskId/?_id=${taskId}`,
        method: 'GET',
      }),
      providesTags: (result, error, taskId) => [{ type: 'taskComments', id: taskId }],
    }),

    addTaskComment: builder.mutation({
      query: data => ({
        url: `/TaskComments/Admin/Create`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { taskId }) => [{ type: 'taskComments', id: taskId }],
    }),

    // * Companies related
    getCompanies: builder.query({
      query: ({ page, limit, search }) => ({
        url: `/companies/pagination`,
        method: 'POST',
        body: {
          page,
          limit,
          ...(search && { search }),
        },
      }),
      providesTags: ['companies'],
    }),

    createCompany: builder.mutation({
      query: name => ({
        url: `/companies`,
        method: 'POST',
        body: { name: name },
      }),
      invalidatesTags: ['companies'],
    }),

    updateCompany: builder.mutation({
      query: ({ id, name }) => ({
        url: `/companies/?companyId=${id}`,
        method: 'PUT',
        body: { name: name },
      }),
      invalidatesTags: ['companies'],
    }),

    deleteCompany: builder.mutation({
      query: id => ({
        url: `/companies/?companyId=${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['companies'],
    }),

    setCompanyUsers: builder.mutation({
      query: ({ id, userIds }) => ({
        url: `/companies/bulkUpdateUsers/?companyId=${id}`,
        method: 'PUT',
        body: { userIds: userIds },
      }),
      invalidatesTags: ['companies', 'users'],
    }),

    activateUser: builder.mutation({
      query: id => ({
        url: `user/admin/activate?userId=${id}`,
        method: 'PUT',
      }),
      invalidatesTags: ['users'],
    }),

    deactivateUser: builder.mutation({
      query: id => ({
        url: `user/admin/deactivate?userId=${id}`,
        method: 'PUT',
      }),
      invalidatesTags: ['users'],
    }),

    getTeams: builder.query({
      query: domainHost => {
        const params = domainHost ? `?domainHost=${encodeURIComponent(domainHost)}` : '';
        return {
          url: `/Team/Admin${params}`,
          method: 'GET',
        };
      },
      providesTags: ['teams'],
    }),

    getAdminDomains: builder.query({
      query: () => ({
        url: '/Domain/Admin/FindAll',
        method: 'GET',
      }),
    }),

    getTeamsByCompany: builder.query({
      query: companyId => ({
        url: `/Team/Admin/GetTeamsByCompanyId?companyId=${companyId}`,
        method: 'GET',
      }),
      providesTags: ['teams'],
    }),

    getAdminTeamMembers: builder.query({
      query: id => ({
        url: `/Team/Admin/TeamMember?teamId=${id}`,
        method: 'GET',
      }),
      providesTags: ['teamMembers'],
    }),

    createTeam: builder.mutation({
      query: body => ({
        url: '/Team/Admin',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['teams'],
    }),
    updateTeam: builder.mutation({
      query: body => ({
        url: `/Team/Admin`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['teams'],
    }),

    getTeamCredits: builder.query({
      query: () => ({
        url: '/credits/admin/teams',
        method: 'GET',
      }),
      providesTags: ['teamCredits'],
    }),

    updateTeamCreditsHistory: builder.mutation({
      query: ({ id, body }) => ({
        url: `/credits/admin/teams/purchase-history?creditPurchaseId=${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['teamMembers'],
    }),

    getTeamCreditsHistory: builder.query({
      query: id => ({
        url: `/credits/admin/team/history?team_id=${id}`,
        method: 'GET',
      }),
      providesTags: ['teamMembers'],
    }),

    adjustTeamCredits: builder.mutation({
      query: data => ({
        url: '/credits/admin/teams',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['teamCredits'],
    }),

    getCustomWork: builder.query({
      query: () => ({
        url: '/credits/admin/team/custom-work',
        method: 'GET',
      }),
      transformResponse: response => response?.data ?? [],
      providesTags: ['teamCredits'],
    }),

    addCustomWork: builder.mutation({
      query: data => ({
        url: '/credits/admin/team/custom-work',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['teamCredits'],
    }),

    editCustomWork: builder.mutation({
      query: data => ({
        url: '/credits/admin/team/custom-work',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['teamCredits'],
    }),

    deleteCustomWork: builder.mutation({
      query: id => ({
        url: `/credits/admin/team/custom-work?creditUsageId=${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['teamCredits'],
    }),
    getPlaybooks: builder.query({
      query: () => ({
        url: '/playbooks/admin',
        method: 'GET',
      }),
      providesTags: ['playbooks'],
    }),
    enablePlaybookForTeam: builder.mutation({
      query: ({ teamId, playbookId }) => ({
        url: `/playbooks/admin/team/enable?teamId=${teamId}&playbookId=${playbookId}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { teamId }) => [{ type: 'teamPlaybooks', id: teamId }],
    }),
    disablePlaybookForTeam: builder.mutation({
      query: ({ teamId, playbookId }) => ({
        url: `/playbooks/admin/team/disable?teamId=${teamId}&playbookId=${playbookId}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { teamId }) => [{ type: 'teamPlaybooks', id: teamId }],
    }),
    getTeamPlaybooks: builder.query({
      query: teamId => ({
        url: `/playbooks/admin/team?teamId=${teamId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      providesTags: (result, error, teamId) => [{ type: 'teamPlaybooks', id: teamId }],
    }),

    crawlBranding: builder.mutation({
      query: data => ({
        url: '/Team/Admin/VisualizationConfig/Suggest',
        method: 'POST',
        body: data,
      }),
    }),

    oAuthAuthorize: builder.mutation({
      query: data => ({
        url: '/admin/oauth/authorize/approve',
        method: 'POST',
        body: data,
      }),
    }),

    // * Agent reports
    createAgentReport: builder.mutation({
      query: body => ({
        url: '/Reporting/Admin/reports',
        method: 'POST',
        body,
      }),
    }),

    getAgentReportStatus: builder.query({
      query: reportId => ({
        url: `/Reporting/Admin/reports/${encodeURIComponent(reportId)}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      providesTags: (result, error, reportId) => [{ type: 'agentReports', id: reportId }],
    }),

    getAgentReportResult: builder.query({
      query: reportId => ({
        url: `/Reporting/Admin/reports/${encodeURIComponent(reportId)}/result`,
        method: 'GET',
      }),
      providesTags: (result, error, reportId) => [{ type: 'agentReports', id: reportId }],
    }),

    getAgentReportLeads: builder.mutation({
      query: body => ({
        url: '/Reporting/Admin/leads',
        method: 'POST',
        body,
      }),
    }),

    getAgentVisitorFrequency: builder.query({
      query: ({ agentId, startDate, endDate }) => ({
        url: '/Reporting/Admin/visitor-frequency',
        method: 'GET',
        params: { agentId, startDate, endDate },
      }),
    }),
  }),
});

// Export hooks for each endpoint
export const {
  useLoginMutation,
  useUserInfoQuery,
  useFindAllUsersQuery,
  useAddUserMutation,
  useLazyGetUserForUpdateByAdminQuery,
  useUpdateUserByAdminMutation,
  useGenerateUserTokenMutation,
  useGetAllUserDomainsMutation,
  useGetCustomerReportMutation,
  useGetResearchPartnerDateMutation,
  useGetDiscoveryAgentDataMutation,
  useGetSummaryUsageDataMutation,
  useGetSEOV3DataMutation,
  useGetSEOV4DataMutation,
  useGetBacklinkUsageDataMutation,
  useGetDefaultChecklistQuery,
  useAddDefaultChecklistMutation,
  useDeleteDefaultChecklistMutation,
  useUpdateDefaultChecklistMutation,
  useUploadImageMutation,
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useUpdateTaskMutation,
  useGetUserChecklistQuery,
  useGetTeamMembersQuery,
  useAddTeamMemberMutation,
  useDeleteTeamMemberMutation,
  useDeleteUserChecklistMutation,
  useUpdateUserChecklistMutation,
  useGetUserChecklistTaskCommentsQuery,
  useAddTaskCommentMutation,

  // * Companies related
  useGetCompaniesQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useSetCompanyUsersMutation,
  useGetUserAdminPaginationMutation,
  useGetUsersWithNoTeamQuery,
  useLazyGetUsersWithNoTeamQuery,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useGetTeamCreditsQuery,
  useGetTeamCreditsHistoryQuery,
  useUpdateTeamCreditsHistoryMutation,
  useAdjustTeamCreditsMutation,
  useGetCustomWorkQuery,
  useAddCustomWorkMutation,
  useDeleteCustomWorkMutation,
  useEditCustomWorkMutation,
  useGetTeamsQuery,
  useGetAdminDomainsQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useFindAllUsersPaginationMutation,
  useGetAdminTeamMembersQuery,
  useGetTeamsByCompanyQuery,
  useGetPlaybooksQuery,
  useEnablePlaybookForTeamMutation,
  useDisablePlaybookForTeamMutation,
  useGetTeamPlaybooksQuery,
  useLazyGetTeamPlaybooksQuery,
  useCrawlBrandingMutation,
  useOAuthAuthorizeMutation,

  // * Agent reports
  useCreateAgentReportMutation,
  useGetAgentReportStatusQuery,
  useGetAgentReportResultQuery,
  useLazyGetAgentVisitorFrequencyQuery,
  useGetAgentReportLeadsMutation,
} = bridgedApi;
